import { Injectable } from '@angular/core';

import {
  Action,
  Selector,
  State,
  StateContext,
  StateToken,
  Store
} from '@ngxs/store';
import { EMPTY, tap } from 'rxjs';

import { BookHttpService } from '@app/core/services/http/book-http.service';
import { MyWorldHttpService } from '@app/core/services/http/my-world-http.service';
import { ToastService } from '@app/core/ui/components/toast';
import { LoadingStateEnum } from '@app/shared/enums/loading-state.enum';
import { catchRemoteError } from '@app/shared/rxjs/custom-operators';
import { RequestState } from '@app/shared/types/request-state';

import {
  LoadStudentByClassAndGradeAction,
  LoadStudentByTokenAction,
  UpdateStudentBiographyAction
} from './student.action';
import { PageSchemaEnum, StudentModel } from './student.model';
import { SetStudentSessionAction } from '../student-session/student-session.action';
import { StudentSessionState } from '../student-session/student-session.state';

type Student = {
  id: number;
  name: string;
  image: string;
  pass: string;
  schoolName: string;
  shopLimitDate: string | null;
  hideSalesEndDateForFamily: boolean;
  bookTitle: string;
  flipbookId: number | null;
  flipbookUrl: string | null;
  pages: StudentModel['pages'];
  teacher: StudentModel['teacher'];
};

export interface StudentStateModel {
  student: Student | null;
  requestStatus: RequestState;
  updateStatus: RequestState;
  flipbookLoading: RequestState;
}

const STUDENT_STATE_TOKEN = new StateToken<StudentStateModel>('student');

const initialState: Partial<StudentStateModel> = {
  student: null
};

@State<StudentStateModel>({
  name: STUDENT_STATE_TOKEN,
  defaults: {
    ...(initialState as StudentStateModel),
    requestStatus: {
      errors: [],
      status: LoadingStateEnum.idle
    },
    updateStatus: {
      key: '',
      errors: [],
      status: LoadingStateEnum.idle
    },
    flipbookLoading: {
      key: '',
      errors: [],
      status: LoadingStateEnum.idle
    }
  }
})
@Injectable()
export class StudentState {
  constructor(
    private http: MyWorldHttpService,
    private bookHttp: BookHttpService,
    private toast: ToastService,
    private store: Store
  ) {}

  @Selector() static student({ student }: StudentStateModel) {
    return student ?? null;
  }

  @Selector() static studentTeacher({ student }: StudentStateModel) {
    return student?.teacher ?? null;
  }

  @Selector() static shopLimitDate({ student }: StudentStateModel) {
    return student?.shopLimitDate ?? null;
  }

  @Selector() static hideSalesEndDateForFamily({ student }: StudentStateModel) {
    return student?.hideSalesEndDateForFamily ?? false;
  }

  @Selector() static biography({ student }: StudentStateModel) {
    const biographyPage = student?.pages?.find(
      ({ schema }) => schema === PageSchemaEnum.BIOGRAPHY
    );
    return biographyPage;
  }

  @Selector() static flipbookUrl({ student }: StudentStateModel) {
    return student?.flipbookUrl ?? null;
  }

  @Selector() static isLoading({ requestStatus }: StudentStateModel) {
    return requestStatus.status === LoadingStateEnum.loading;
  }

  @Selector() static requestStatus({ requestStatus }: StudentStateModel) {
    return requestStatus;
  }

  @Selector() static updateStatus({ updateStatus }: StudentStateModel) {
    return updateStatus;
  }

  @Selector() static flipbookLoading({ flipbookLoading }: StudentStateModel) {
    return flipbookLoading?.status === LoadingStateEnum.loading;
  }

  // eslint-disable-next-line max-lines-per-function
  @Action(LoadStudentByTokenAction) loadStudentByToken(
    { patchState, dispatch }: StateContext<StudentStateModel>,
    { payload }: LoadStudentByTokenAction
  ) {
    patchState({
      requestStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });

    const pass =
      payload?.pass ||
      this.store.selectSnapshot(StudentSessionState.student)?.pass ||
      '';

    return this.http.getStudent(pass).pipe(
      catchRemoteError(
        (remoteError) => {
          this.toast.danger(remoteError.message);
          patchState({
            ...initialState,
            requestStatus: {
              errors: [],
              status: LoadingStateEnum.error
            }
          });
        },
        {
          defaultMessage:
            'Não foi possível encontrar o aluno. Por favor, tente novamente.'
        }
      ),
      tap(({ token, ...data }) => {
        const student = this.toStudent(data);

        patchState({
          student,
          requestStatus: {
            errors: [],
            status: LoadingStateEnum.loaded
          }
        });
        dispatch(
          new SetStudentSessionAction({
            id: student.id,
            name: student.name,
            pass: student.pass,
            token
          })
        );

        if (!student.flipbookUrl) {
          patchState({
            flipbookLoading: {
              errors: [],
              status: LoadingStateEnum.loading
            }
          });
          this.bookHttp.generateFlipbook(student.pass).subscribe((url) => {
            patchState({
              student: {
                ...student,
                flipbookUrl: url
              },
              flipbookLoading: {
                errors: [],
                status: LoadingStateEnum.loaded
              }
            });
          });
        }
      })
    );
  }

  // eslint-disable-next-line max-lines-per-function
  @Action(LoadStudentByClassAndGradeAction) loadStudentByClassAndGrade(
    { patchState, dispatch }: StateContext<StudentStateModel>,
    { payload }: LoadStudentByClassAndGradeAction
  ) {
    patchState({
      requestStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });
    return this.http.getStudentDataBySchoolAndGrade(payload).pipe(
      catchRemoteError(
        (remoteError) => {
          this.toast.danger(remoteError.message);
          patchState({
            ...initialState,
            requestStatus: {
              errors: [],
              status: LoadingStateEnum.error
            }
          });
        },
        {
          defaultMessage:
            'O aluno não pertence à esta escola ou esta série. Por favor, tente novamente'
        }
      ),
      tap(({ token, ...data }) => {
        const student = this.toStudent(data);

        patchState({
          student,
          requestStatus: {
            errors: [],
            status: LoadingStateEnum.loaded
          }
        });
        dispatch(
          new SetStudentSessionAction({
            id: student.id,
            name: student.name,
            pass: student.pass,
            token
          })
        );

        if (!student.flipbookUrl) {
          patchState({
            flipbookLoading: {
              errors: [],
              status: LoadingStateEnum.loading
            }
          });
          this.bookHttp.generateFlipbook(student.pass).subscribe((url) => {
            patchState({
              student: {
                ...student,
                flipbookUrl: url
              },
              flipbookLoading: {
                errors: [],
                status: LoadingStateEnum.loaded
              }
            });
          });
        }
      })
    );
  }

  // eslint-disable-next-line max-lines-per-function
  @Action(UpdateStudentBiographyAction)
  UpdateStudentBiographyAction(
    { patchState, getState, dispatch }: StateContext<StudentStateModel>,
    { payload }: UpdateStudentBiographyAction
  ) {
    patchState({
      updateStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });

    const studentBiographyPageSchema = getState().student?.pages?.find(
      ({ schema }) => schema === PageSchemaEnum.BIOGRAPHY
    );

    if (!studentBiographyPageSchema) {
      const errorMessage = 'Não foi possível encontrar a biografia do aluno.';

      this.toast.danger(errorMessage);

      patchState({
        updateStatus: {
          errors: [
            {
              detail: errorMessage
            }
          ],
          status: LoadingStateEnum.error
        }
      });

      return EMPTY;
    }

    return this.http
      .updateStudentBiography(studentBiographyPageSchema.id, payload)
      .pipe(
        catchRemoteError(
          (remoteError) => {
            this.toast.danger(remoteError.message);
            patchState({
              updateStatus: {
                errors: [
                  {
                    detail: remoteError.message
                  }
                ],
                status: LoadingStateEnum.error
              }
            });
          },
          { defaultMessage: 'Não foi possível atualizar a biografia do aluno.' }
        ),
        tap(() => {
          patchState({
            updateStatus: {
              errors: [],
              status: LoadingStateEnum.loaded
            }
          });
          dispatch(
            new LoadStudentByTokenAction({
              pass: getState().student?.pass
            })
          );
        })
      );
  }

  private toStudent(data: Omit<StudentModel, 'token'>): Student {
    return {
      id: data.id,
      name: data.name,
      image: data.image,
      pass: data.pass,
      teacher: data.teacher,
      schoolName: data.schoolName,
      shopLimitDate: data.shopLimitDate,
      hideSalesEndDateForFamily: data.hideSalesEndDateForFamily ?? false,
      pages: data.pages ?? [],
      flipbookId: data.flipBookId,
      flipbookUrl: data.flipBookUrl,
      bookTitle:
        data.pages.find((page) => page.schema === PageSchemaEnum.COVER)?.value
          ?.text ?? ''
    };
  }
}
